import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import { userApi, ApiError } from '../../../services';
import styles from './ProfileLinkedAccountsScreen.module.css';

interface LinkedAccount {
  id: string;
  accountType: 'personal' | 'employer';
  authProvider: 'google' | 'microsoft' | 'phone';
  email?: string;
  phoneNumber?: string;
  linkedAt: string;
  isPrimary?: boolean;
}

interface LinkableProvider {
  id: string;
  name: string;
  icon: React.ReactElement;
  authProvider: 'google' | 'microsoft' | 'phone';
  accountType: 'personal' | 'employer';
}

const ProfileLinkedAccountsScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const [linkingProvider, setLinkingProvider] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const linkableProviders: LinkableProvider[] = [
    {
      id: 'google-personal',
      name: 'Google (Personal)',
      icon: <GoogleIcon />,
      authProvider: 'google',
      accountType: 'personal'
    },
    {
      id: 'microsoft-employer',
      name: 'Microsoft (Work)',
      icon: <MicrosoftIcon />,
      authProvider: 'microsoft',
      accountType: 'employer'
    },
    {
      id: 'phone-personal',
      name: 'Phone Number',
      icon: <PhoneIcon />,
      authProvider: 'phone',
      accountType: 'personal'
    }
  ];

  useEffect(() => {
    fetchLinkedAccounts();
  }, []);

  const fetchLinkedAccounts = async () => {
    try {
      const result = await userApi.getLinkedAccounts();
      
      if (result.success && result.data) {
        setLinkedAccounts(result.data);
      }
    } catch (error) {
      console.error('Error fetching linked accounts:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return <GoogleIcon className={styles.providerIcon} />;
      case 'microsoft':
        return <MicrosoftIcon className={styles.providerIcon} />;
      case 'phone':
        return <PhoneIcon className={styles.providerIcon} />;
      default:
        return <LinkIcon className={styles.providerIcon} />;
    }
  };

  const getAccountTypeIcon = (type: string) => {
    return type === 'employer' ? 
      <BusinessIcon className={styles.typeIcon} /> : 
      <PersonIcon className={styles.typeIcon} />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAccountDisplay = (account: LinkedAccount) => {
    if (account.email) {
      return account.email;
    } else if (account.phoneNumber) {
      return account.phoneNumber;
    }
    return 'Unknown Account';
  };

  const handleLinkAccount = async (provider: LinkableProvider) => {
    setLinkingProvider(provider.id);
    setLinkSuccess(false);
    
    try {
      // In production, this would initiate OAuth flow
      const result = await userApi.linkAccount({
        authProvider: provider.authProvider,
        accountType: provider.accountType
      });
      
      if (result.success) {
        await fetchLinkedAccounts();
        setLinkSuccess(true);
        setTimeout(() => setLinkSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error linking account:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
    } finally {
      setLinkingProvider(null);
    }
  };

  const handleUnlinkAccount = async (accountId: string) => {
    const account = linkedAccounts.find(acc => acc.id === accountId);
    if (account?.isPrimary) {
      alert('Cannot unlink your primary account. Please link another account first.');
      return;
    }

    if (!window.confirm('Are you sure you want to unlink this account? You will lose access to any data specific to this account.')) {
      return;
    }
    
    setUnlinkingId(accountId);
    
    try {
      const result = await userApi.unlinkAccount(accountId);
      
      if (result.success) {
        await fetchLinkedAccounts();
      }
    } catch (error) {
      console.error('Error unlinking account:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
    } finally {
      setUnlinkingId(null);
    }
  };

  const isProviderLinked = (provider: LinkableProvider) => {
    return linkedAccounts.some(
      account => account.authProvider === provider.authProvider && 
                 account.accountType === provider.accountType
    );
  };

  if (isLoading) {
    return (
      <div className={styles.profileLinkedAccounts}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileLinkedAccounts}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Linked Accounts</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        <div className={styles.infoCard}>
          <LinkIcon className={styles.infoIcon} />
          <div>
            <h3 className={styles.infoTitle}>Why link accounts?</h3>
            <p className={styles.infoText}>
              Link your personal and work accounts to access all your healthcare benefits in one place. 
              View claims, coverage, and savings across all your insurance plans seamlessly.
            </p>
          </div>
        </div>

        <div className={styles.linkedSection}>
          <h2 className={styles.sectionTitle}>Currently Linked</h2>
          {linkedAccounts.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyText}>No linked accounts yet</p>
            </div>
          ) : (
            <div className={styles.accountsList}>
              {linkedAccounts.map(account => (
                <div key={account.id} className={styles.accountCard}>
                  <div className={styles.accountHeader}>
                    <div className={styles.accountIcons}>
                      {getProviderIcon(account.authProvider)}
                      {getAccountTypeIcon(account.accountType)}
                    </div>
                    {account.isPrimary && (
                      <span className={styles.primaryBadge}>Primary</span>
                    )}
                  </div>
                  <div className={styles.accountInfo}>
                    <p className={styles.accountEmail}>{getAccountDisplay(account)}</p>
                    <p className={styles.accountMeta}>
                      {account.accountType === 'employer' ? 'Work Account' : 'Personal Account'} • 
                      Linked {formatDate(account.linkedAt)}
                    </p>
                  </div>
                  <button
                    className={styles.unlinkButton}
                    onClick={() => handleUnlinkAccount(account.id)}
                    disabled={unlinkingId === account.id || account.isPrimary}
                    title={account.isPrimary ? 'Cannot unlink primary account' : 'Unlink this account'}
                  >
                    {unlinkingId === account.id ? (
                      <CircularProgress size={20} />
                    ) : (
                      <>
                        <LinkOffIcon />
                        <span>Unlink</span>
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {linkSuccess && (
          <div className={styles.successMessage}>
            <CheckCircleIcon className={styles.successIcon} />
            Account linked successfully!
          </div>
        )}

        <div className={styles.availableSection}>
          <h2 className={styles.sectionTitle}>Available to Link</h2>
          <div className={styles.providersList}>
            {linkableProviders.map(provider => {
              const isLinked = isProviderLinked(provider);
              return (
                <button
                  key={provider.id}
                  className={`${styles.providerCard} ${isLinked ? styles.providerLinked : ''}`}
                  onClick={() => !isLinked && handleLinkAccount(provider)}
                  disabled={isLinked || linkingProvider === provider.id}
                >
                  <div className={styles.providerContent}>
                    {provider.icon}
                    <span className={styles.providerName}>{provider.name}</span>
                  </div>
                  {linkingProvider === provider.id ? (
                    <CircularProgress size={20} />
                  ) : isLinked ? (
                    <CheckCircleIcon className={styles.linkedIcon} />
                  ) : (
                    <AddIcon className={styles.addIcon} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.securitySection}>
          <h3 className={styles.securityTitle}>Account Security</h3>
          <ul className={styles.securityList}>
            <li>We never store your passwords</li>
            <li>Authentication is handled securely by your provider (Google, Microsoft, etc.)</li>
            <li>You can unlink accounts at any time</li>
            <li>Your data remains separate between linked accounts</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProfileLinkedAccountsScreen;